import React, { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { Copy, Check, User, Sparkles } from 'lucide-react'

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <button onClick={handleCopy} style={copyBtnStyle} title="Copy code">
      {copied ? <Check size={13} color="var(--accent)" /> : <Copy size={13} />}
    </button>
  )
}

const CodeBlock = ({ children, className }) => {
  const lang = /language-(\w+)/.exec(className || '')?.[1] || 'text'
  const code = String(children).replace(/\n$/, '')
  return (
    <div style={{ position: 'relative', margin: '0.75rem 0', borderRadius: '10px', overflow: 'hidden' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: '#1a1a2e', padding: '7px 14px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <span style={{ fontSize: 11, color: '#7b7b9b', fontFamily: 'var(--font-mono)', fontWeight: 500 }}>
          {lang}
        </span>
        <CopyButton text={code} />
      </div>
      <SyntaxHighlighter
        language={lang}
        style={oneDark}
        customStyle={{
          margin: 0,
          padding: '14px 16px',
          background: '#1e1e2e',
          fontSize: '13px',
          lineHeight: 1.6,
          borderRadius: 0,
        }}
        codeTagProps={{ style: { fontFamily: 'var(--font-mono)' } }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  )
}

const InlineCode = ({ children }) => (
  <code style={{
    background: 'rgba(255,255,255,0.08)',
    padding: '0.15em 0.45em',
    borderRadius: '4px',
    fontSize: '0.875em',
    fontFamily: 'var(--font-mono)',
    color: '#e06c75',
  }}>
    {children}
  </code>
)

const markdownComponents = {
  code({ node, inline, className, children, ...props }) {
    if (inline) return <InlineCode>{children}</InlineCode>
    return <CodeBlock className={className}>{children}</CodeBlock>
  },
  p: ({ children }) => <p style={{ marginBottom: '0.65rem', lineHeight: 1.75 }}>{children}</p>,
  ul: ({ children }) => <ul style={{ paddingLeft: '1.4rem', marginBottom: '0.65rem' }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ paddingLeft: '1.4rem', marginBottom: '0.65rem' }}>{children}</ol>,
  li: ({ children }) => <li style={{ marginBottom: '0.25rem', lineHeight: 1.7 }}>{children}</li>,
  h1: ({ children }) => <h1 style={{ fontSize: '1.4rem', fontWeight: 600, margin: '1.2rem 0 0.5rem', color: 'var(--text-primary)' }}>{children}</h1>,
  h2: ({ children }) => <h2 style={{ fontSize: '1.2rem', fontWeight: 600, margin: '1.1rem 0 0.4rem', color: 'var(--text-primary)' }}>{children}</h2>,
  h3: ({ children }) => <h3 style={{ fontSize: '1.05rem', fontWeight: 600, margin: '1rem 0 0.4rem', color: 'var(--text-primary)' }}>{children}</h3>,
  blockquote: ({ children }) => (
    <blockquote style={{
      borderLeft: '3px solid var(--accent)',
      paddingLeft: '1rem',
      color: 'var(--text-secondary)',
      margin: '0.75rem 0',
      fontStyle: 'italic',
    }}>
      {children}
    </blockquote>
  ),
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer" style={{ color: 'var(--text-primary)', textDecoration: 'underline' }}
       onMouseEnter={e => e.target.style.textDecoration = 'underline'}
       onMouseLeave={e => e.target.style.textDecoration = 'underline'}>
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div style={{ overflowX: 'auto', margin: '0.75rem 0' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 14 }}>{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th style={{ border: '1px solid var(--border)', padding: '8px 12px', background: 'var(--bg-input)', fontWeight: 600, textAlign: 'left' }}>
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td style={{ border: '1px solid var(--border)', padding: '8px 12px' }}>{children}</td>
  ),
  hr: () => <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '1rem 0' }} />,
}

const formatTime = (dateStr) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })
}

export default function MessageBubble({ message }) {
  const isUser = message.role === 'user'
  const [showActions, setShowActions] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      style={{
        display: 'flex',
        gap: '14px',
        padding: '20px 0',
        animation: 'fadeInUp 0.25s ease',
        flexDirection: 'row',
        alignItems: 'flex-start',
      }}
    >
      {/* Avatar */}
      <div style={{
        width: 34,
        height: 34,
        borderRadius: isUser ? '50%' : '10px',
        background: isUser ? 'var(--bg-input)' : 'var(--accent)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginTop: 2,
        border: isUser ? '1px solid var(--border)' : 'none',
      }}>
        {isUser
          ? <User size={16} color="var(--text-secondary)" />
          : <img src='/images/fmt_logo.png' width={32} />
        }
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '6px',
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
            {isUser ? 'Korisnik' : 'FMT Asistent'}
          </span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {formatTime(message.createdAt)}
          </span>
        </div>

        <div style={{
          color: 'var(--text-primary)',
          lineHeight: 1.75,
          fontSize: 15,
          wordBreak: 'break-word',
        }}>
          {isUser ? (
            <span style={{ whiteSpace: 'pre-wrap' }}>{message.content}</span>
          ) : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {message.content}
            </ReactMarkdown>
          )}
        </div>

        {/* Copy action */}
        {showActions && (
          <div style={{ marginTop: '8px', display: 'flex', gap: '6px' }}>
            <button
              onClick={handleCopy}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                padding: '4px 9px',
                color: 'var(--text-muted)',
                fontSize: 12,
                cursor: 'pointer',
                fontFamily: 'var(--font-sans)',
                transition: 'all var(--transition)',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-primary)' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)' }}
            >
              {copied ? <Check size={12} color="var(--accent)" /> : <Copy size={12} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const copyBtnStyle = {
  background: 'transparent',
  border: 'none',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  padding: '3px',
  display: 'flex',
  alignItems: 'center',
  borderRadius: '4px',
  transition: 'color var(--transition)',
}
